<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark>
        <v-toolbar-title>프롬북 · 유저 관리</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip small outlined dark class="mr-2">
          <v-icon left x-small>mdi-account-group</v-icon>
          총 {{ total.toLocaleString() }}명
        </v-chip>
      </v-toolbar>

      <!-- 필터/검색 영역 -->
      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.search_type"
              :items="searchTypeOptions"
              label="검색 타입"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="filter.keyword"
              label="검색어"
              outlined dense hide-details clearable
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="onSearch"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.status"
              :items="statusOptions"
              label="상태"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="1">
            <v-btn color="primary" depressed block @click="onSearch">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- 유저 리스트 -->
      <v-card-text class="pa-0 pt-2">
        <div style="overflow-x: auto;">
          <v-data-table
            :headers="headers"
            :items="list"
            :items-per-page="50"
            :footer-props="{'items-per-page-options':[50, 100, 200]}"
            :loading="loading"
            :server-items-length="total"
            class="elevation-0"
          >
            <template v-slot:item.status="{ item }">
              <v-chip small :color="item.status === 'active' ? 'green' : item.status === 'ban' ? 'red' : 'grey'" dark>
                {{ item.status === 'active' ? '활성' : item.status === 'ban' ? '정지' : '비활성' }}
              </v-chip>
            </template>
            <template v-slot:item.point="{ item }">
              {{ (item.point || 0).toLocaleString() }}
            </template>
            <template v-slot:item.total_earn_point="{ item }">
              {{ (item.total_earn_point || 0).toLocaleString() }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small @click="openDetail(item)" title="상세보기">
                <v-icon small>mdi-magnify</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
        <v-container v-if="!loading && list.length === 0" class="text-center py-8">
          <v-icon size="56" color="grey lighten-1">mdi-account-search</v-icon>
          <p class="text-body-1 grey--text mt-3">검색어를 입력한 뒤 검색해주세요.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- 유저 상세 다이얼로그 -->
    <v-dialog v-model="dialogDetail" max-width="700" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-account</v-icon>
          유저 상세 정보
          <v-spacer></v-spacer>
          <v-chip small outlined>{{ selectedUser.user_id }}</v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <!-- 기본 정보 -->
          <v-simple-table dense>
            <tbody>
              <tr><td class="font-weight-bold" style="width:140px">유저 아이디</td><td>{{ selectedUser.user_id }}</td></tr>
              <tr><td class="font-weight-bold">레거시 ID</td><td>{{ selectedUser.legacy_user_id }}</td></tr>
              <tr><td class="font-weight-bold">닉네임</td><td>{{ selectedUser.nickname }}</td></tr>
              <tr><td class="font-weight-bold">전화번호</td><td>{{ selectedUser.phone }}</td></tr>
              <tr><td class="font-weight-bold">잔액 포인트</td><td>{{ (selectedUser.point || 0).toLocaleString() }}</td></tr>
              <tr><td class="font-weight-bold">총 적립 포인트</td><td>{{ (selectedUser.total_earn_point || 0).toLocaleString() }}</td></tr>
              <tr><td class="font-weight-bold">최종 접속</td><td>{{ selectedUser.last_login_at || '-' }}</td></tr>
              <tr><td class="font-weight-bold">상태</td>
                <td>
                  <v-chip small :color="selectedUser.status === 'active' ? 'green' : 'red'" dark>
                    {{ selectedUser.status === 'active' ? '활성' : '정지' }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <!-- 탭: 포인트 로그 / 활동 로그 / 인증 로그 -->
          <v-tabs v-model="detailTab" class="mt-4" dense>
            <v-tab>포인트 로그</v-tab>
            <v-tab>활동 로그</v-tab>
            <v-tab>인증 로그</v-tab>
          </v-tabs>
          <v-tabs-items v-model="detailTab">
            <!-- 포인트 로그 -->
            <v-tab-item>
              <v-simple-table dense class="mt-2">
                <thead>
                  <tr>
                    <th>ID</th><th>변동량</th><th>사유</th><th>일시</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in (detail.point_log || [])" :key="log.id">
                    <td>{{ log.id }}</td>
                    <td :class="log.amount >= 0 ? 'green--text' : 'red--text'">{{ log.amount }}</td>
                    <td>{{ log.reason }}</td>
                    <td>{{ log.created_at }}</td>
                  </tr>
                  <tr v-if="!(detail.point_log || []).length">
                    <td colspan="4" class="text-center grey--text py-4">데이터 없음</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-tab-item>
            <!-- 활동 로그 -->
            <v-tab-item>
              <v-simple-table dense class="mt-2">
                <thead>
                  <tr>
                    <th>ID</th><th>타입</th><th>내용</th><th>일시</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in (detail.activity_log || [])" :key="log.id">
                    <td>{{ log.id }}</td>
                    <td>{{ log.type }}</td>
                    <td>{{ log.content }}</td>
                    <td>{{ log.created_at }}</td>
                  </tr>
                  <tr v-if="!(detail.activity_log || []).length">
                    <td colspan="4" class="text-center grey--text py-4">데이터 없음</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-tab-item>
            <!-- 인증 로그 -->
            <v-tab-item>
              <v-simple-table dense class="mt-2">
                <thead>
                  <tr>
                    <th>ID</th><th>타입</th><th>IP</th><th>일시</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in (detail.auth_log || [])" :key="log.id">
                    <td>{{ log.id }}</td>
                    <td>{{ log.type }}</td>
                    <td>{{ log.ip }}</td>
                    <td>{{ log.created_at }}</td>
                  </tr>
                  <tr v-if="!(detail.auth_log || []).length">
                    <td colspan="4" class="text-center grey--text py-4">데이터 없음</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn small color="red" dark @click="banUser" v-if="selectedUser.status === 'active'">
            <v-icon left small>mdi-cancel</v-icon>이용 정지
          </v-btn>
          <v-btn small color="green" dark @click="unbanUser" v-if="selectedUser.status === 'ban'">
            <v-icon left small>mdi-check-circle</v-icon>정지 해제
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDetail = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 정지 사유 다이얼로그 -->
    <v-dialog v-model="dialogBan" max-width="400">
      <v-card>
        <v-card-title>이용 정지</v-card-title>
        <v-card-text>
          <v-text-field v-model="banReason" label="정지 사유" outlined dense></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogBan = false">취소</v-btn>
          <v-btn color="red" dark @click="confirmBan">정지</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="py-12"></div>
    <div class="py-12"></div>
  </div>
</template>

<script>
import { prombook } from '../../api'

export default {
  name: 'Prombook_UserList',
  data() {
    return {
      loading: false,
      total: 0,
      list: [],
      filter: {
        search_type: 'user_id',
        keyword: '',
        status: null,
        page: 1,
        page_size: 50,
      },
      dialogDetail: false,
      dialogBan: false,
      banReason: '',
      detailTab: 0,
      selectedUser: {},
      detail: {},
      searchTypeOptions: [
        { text: '유저 ID', value: 'user_id' },
        { text: '닉네임', value: 'nickname' },
        { text: '전화번호', value: 'phone' },
      ],
      statusOptions: [
        { text: '전체', value: null },
        { text: '활성', value: 'active' },
        { text: '정지', value: 'ban' },
      ],
      headers: [
        { text: '유저 아이디', value: 'user_id', width: '140px' },
        { text: '레거시 ID', value: 'legacy_user_id', width: '120px' },
        { text: '닉네임', value: 'nickname', width: '120px' },
        { text: '전화번호', value: 'phone', width: '130px' },
        { text: '잔액', value: 'point', width: '100px' },
        { text: '총 적립', value: 'total_earn_point', width: '100px' },
        { text: '최종 접속', value: 'last_login_at', width: '150px' },
        { text: '상태', value: 'status', width: '90px' },
        { text: '상세', value: 'actions', width: '60px', sortable: false },
      ],
    }
  },
  methods: {
    async load() {
      try {
        this.loading = true
        const r = await prombook.search_user(
          this.filter.search_type,
          this.filter.keyword,
          this.filter.status,
          this.filter.page,
          this.filter.page_size
        )
        this.list = r.user_list || []
        this.total = r.total || 0
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      this.filter.page = 1
      this.load()
    },
    async openDetail(item) {
      this.selectedUser = JSON.parse(JSON.stringify(item))
      this.detail = {}
      this.detailTab = 0
      this.dialogDetail = true
      try {
        const r = await prombook.get_user_detail(item.user_id)
        this.detail = r || {}
        if (r && r.user_info) this.selectedUser = r.user_info
        // 추가 응답: point_log_list / activity_log_list / auth_log_list 도 r 안에 있음 (this.detail = r 로 보존됨)
      } catch (e) {
        console.error(e)
      }
    },
    banUser() {
      this.banReason = ''
      this.dialogBan = true
    },
    async confirmBan() {
      try {
        await prombook.ban_user(this.selectedUser.user_id, this.banReason)
        alert('이용 정지 처리되었습니다.')
        this.dialogBan = false
        this.dialogDetail = false
        this.load()
      } catch (e) {
        console.error(e)
        alert('이용 정지에 실패했습니다.')
      }
    },
    async unbanUser() {
      if (!confirm(`유저 [${this.selectedUser.user_id}] 정지를 해제하시겠습니까?`)) return
      try {
        await prombook.unban_user(this.selectedUser.user_id)
        alert('정지 해제되었습니다.')
        this.dialogDetail = false
        this.load()
      } catch (e) {
        console.error(e)
        alert('정지 해제에 실패했습니다.')
      }
    },
  },
  mounted() {
    // 초기 로드 — 빈 검색으로 최근 유저 조회
    this.load()
  },
}
</script>
